import React, { useEffect, useState, useCallback, Suspense } from 'react';
import {
  Button,
  Col,
  ConfigProvider,
  Row,
  Table,
  Tag,
  Tooltip,
  notification,
  Dropdown,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import trTR from 'antd/es/locale/tr_TR';
import userHaveRolePermission from 'helpers/userHaveRolePermission';
import { useHistory } from 'react-router-dom';
import {
  getTaskTemplatesList,
  deleteTaskTemplatesList,
} from 'store/requests/taskPlans';
import { PAGE_URLS } from 'helpers/endpoints';
const StockTrackingFilterForm = React.lazy(() =>
  import('./TaskTemplatesFilterForm')
);

const TaskTemplates = ({ route }) => {
  const location = useLocation();
  const { confirm } = Modal;
  const taskGroupDataItem = location.state?.dataItem;
  const [taskGroupData, setTaskGroupData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const history = useHistory();
  const [selectedItem, setSelectedItem] = useState(null);
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const Add = React.lazy(() => import('./Add'));
  const Edit = React.lazy(() => import('./Edit'));
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [25, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 25,
    },
  });

  const fetchData = useCallback(
    debounce(async (currentFilters = {}, forceReload = false) => {
      if (
        !forceReload &&
        !taskGroupDataItem &&
        Object.keys(currentFilters).length === 0
      ) {
        return;
      }

      setLoading(true);

      const filteredFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(
          ([_, value]) => value !== undefined && value !== null
        )
      );
      const finalFilters = { ...filteredFilters, deleted: false };
      const params = {
        ...(taskGroupDataItem && { taskGroupId: taskGroupDataItem.id }),
        page: tableParams.pagination.current - 1,
        size: tableParams.pagination.pageSize,
        ...filteredFilters,
        ...finalFilters,
      };

      const response = await getTaskTemplatesList(params);
      const sortedData = (response.content || []).sort((a, b) => a.priority - b.priority);
      setTaskGroupData(sortedData || []);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          current: response.page.number + 1,
          total: response.page.totalElements,
        },
      }));
      setLoading(false);
    }, 300),
    [taskGroupDataItem]
  );

  useEffect(() => {
    fetchData(filters);
  }, [filters, fetchData]);

  const handleTableChange = (pagination) => {
    setTableParams({
      pagination,
    });
    fetchData({
      ...filters,
      page: pagination.current - 1,
      size: pagination.pageSize,
    });
  };

  const getSelectedRowData = () => {
    if (selectedRowKeys.length > 0) {
      const selectedId = selectedRowKeys[0];
      return taskGroupData.find((item) => item.id === selectedId);
    }
    return null;
  };

  const showDeleteGroup = () => {
    confirm({
      title: t('modalDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: `${getSelectedRowData()?.name} ${t(
        'taskTemplateDeleteConfirm'
      )}`,
      okText: t('slotManagement.delete'),
      okType: 'primary',
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        const selectedData = getSelectedRowData();
        if (selectedData) {
          const groupId = selectedData.id;
          deleteTaskTemplatesList(groupId).then(() => {
            notification.success({
              message: t('processCompleted'),
              description: t('taskTemplateDeleted'),
            });
            fetchData(filters, true);
            setSelectedRowKeys([]);
          });
        }
      },
      onCancel() {},
    });
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const actions = [
    {
      title: t('addTaskTemplates'),
      icon: <PlusOutlined />,
      type: 'primary',
      onClick: () => setIsAddModalOpen(true),
      disabled: false,
    },
    {
      isCrudEnabled: true,
      type: '',
      title: t('slotManagement.delete'),
      icon: <DeleteOutlined />,
      onClick: showDeleteGroup,
      disabled: selectedRowKeys.length > 1 || selectedRowKeys.length < 1,
      dropdown: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((action, idx) => (
      <Tooltip title={action.title} key={idx}>
        <Button
          type={action.type}
          shape="round"
          icon={action.icon}
          onClick={action.onClick}
          disabled={action.disabled}
          style={{
            marginBottom: '10px',
            marginTop: '20px',
            marginRight: '3px',
          }}
        />
      </Tooltip>
    ));
  };

  const columns = [
    {
      title: t('taskTemplateName'),
      dataIndex: 'name',
      key: 'name',
      width: 400,
      sorter: true,
    },
    {
      title: t('taskPeriod'),
      render: (_, record) => record.period?.name || '-',
      key: 'roleCode',
    },

    {
      title: t('taskPriority'),
      dataIndex: 'priority',
      key: 'priority',
      sorter: (a, b) => a.priority - b.priority,
    },

    {
      title: t('timeWithSecond'),
      dataIndex: 'duration',
      key: 'priority',
    },
    {
      title: t('status'),
      dataIndex: 'deleted',
      key: 'deleted',
      render: (active) =>
        active ? (
          <Tag color={'red'}>{t('passive')}</Tag>
        ) : (
          <Tag color={'green'}>{t('active')}</Tag>
        ),
    },

    {
      width: 50,
      render: (dataItem) => (
        <Dropdown
          menu={{
            items: [
              {
                label: t('edit'),
                icon: <EditOutlined />,
                onClick: () => {
                  setIsEditModalOpen(true);

                  setSelectedItem(dataItem);
                },
              },
            ],
          }}
          placement="bottomRight"
          arrow
          trigger={['click']}
          onClick={() => setSelectedItem(dataItem)}
        >
          <Button shape="circle" icon={<MoreOutlined />}></Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <Suspense fallback={<></>}>
        <StockTrackingFilterForm
          handlerFilter={(filter) => {
            setFilters(filter);
          }}
          initialValues={taskGroupDataItem}
          loading={loading}
        />
      </Suspense>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Col>{renderActionButtons()}</Col>
      </Row>

      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={taskGroupData}
            rowSelection={rowSelection}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey="id"
            scroll={{ y: '70vh' }}
          />
        </ConfigProvider>
      </Row>

      <Suspense fallback={<></>}>
        {isAddModalOpen && (
          <Add
            taskGroupId={taskGroupDataItem?.id}
            onCloseHandler={() => setIsAddModalOpen(false)}
            onSuccessCallback={() => fetchData(filters, true)}
            taskGroupData={taskGroupData}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedTemplate={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={() => fetchData(filters, true)}
            taskGroupData={taskGroupData}
          />
        )}
      </Suspense>
    </div>
  );
};

export default TaskTemplates;
